import {Button, Col, Container, Form, Row} from "react-bootstrap";
import React, {useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {getCourseStudentList} from "../api/CourseApi";
import {toolTipRenderer} from "../utils/gridUtils";
import {getConfLabel, titleCase} from "../utils/usacmUtils";
import {getErrorMessageForField} from "../utils/formUtils";
import {CONF_LABEL_KEY_COURSE, COURSE_GRANT_TYPE_NAMES} from "../constants";

/**
 * Everyone who currently has access to one course (any live CourseAccess row), one row per user.
 * Read-only - see courses_utils.get_course_students_display()
 */
export function CourseStudents({course, onClose}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [studentList, setStudentList] = useState([]);
  const [quickFilter, setQuickFilter] = useState('');
  const [errors, setErrors] = useState([]);
  const gridRef = useRef();
  const hasFewRows = (studentList || []).length < 12;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));

  useEffect(() => {
    if (!course?.id) {
      return;
    }
    getCourseStudentList(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setStudentList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }, [course?.id]);

  function quickFilterChanged(newValue) {
    setQuickFilter(newValue);
    if (gridRef?.current?.api) {
      gridRef.current.api.setGridOption('quickFilterText', newValue);
    }
  }

  function dateToDisplay(date) {
    return date ? date.toLocaleDateString() : '';
  }

  // The date filters pick a day (at midnight), so compare the row's date without its time
  const dateFilterParams = {
    comparator: (filterDate, cellValue) => {
      if (!cellValue) {
        return -1;
      }
      const cellDay = new Date(cellValue.getFullYear(), cellValue.getMonth(), cellValue.getDate());
      return cellDay < filterDate ? -1 : (cellDay > filterDate ? 1 : 0);
    },
  };

  const colDefs = [
    {
      headerName: 'Name', flex: 3, minWidth: 150, cellRenderer: toolTipRenderer, sort: 'asc',
      valueGetter: params => `${params.data?.first_name || ''} ${params.data?.last_name || ''}`.trim(),
    },
    {field: 'email', cellRenderer: toolTipRenderer, headerName: 'Email', flex: 3, minWidth: 150},
    {field: 'affiliation', cellRenderer: toolTipRenderer, headerName: 'Affiliation', flex: 3, minWidth: 120},
    {
      headerName: 'How', flex: 2, minWidth: 130, cellRenderer: toolTipRenderer,
      valueGetter: params => (params.data?.grant_types || [])
        .map(grantType => COURSE_GRANT_TYPE_NAMES[grantType] || grantType).join(', '),
    },
    {
      headerName: 'Price', flex: 2, minWidth: 120, cellRenderer: toolTipRenderer,
      valueGetter: params => (params.data?.price_names || []).join(', '),
    },
    {
      headerName: 'Access Since', width: 140, minWidth: 140,
      valueGetter: params => params.data?.access_since ? new Date(Date.parse(params.data.access_since)) : null,
      valueFormatter: params => dateToDisplay(params.value),
      getQuickFilterText: params => dateToDisplay(params.value),
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
    },
    {
      headerName: 'Expires', width: 140, minWidth: 140,
      valueGetter: params => params.data?.expires_at ? new Date(Date.parse(params.data.expires_at)) : null,
      valueFormatter: params => params.value ? dateToDisplay(params.value) : 'Never',
      getQuickFilterText: params => params.value ? dateToDisplay(params.value) : 'Never',
      // Never expiring sorts after every date
      comparator: (dateA, dateB) => (dateA ? dateA.getTime() : Infinity) - (dateB ? dateB.getTime() : Infinity),
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
    },
    {
      headerName: 'Last Seen', width: 140, minWidth: 140,
      valueGetter: params => params.data?.last_seen ? new Date(Date.parse(params.data.last_seen)) : null,
      valueFormatter: params => params.value ? dateToDisplay(params.value) : 'Never',
      getQuickFilterText: params => params.value ? dateToDisplay(params.value) : 'Never',
      tooltipValueGetter: params => params.value ? params.value.toLocaleString() : '',
      // Never seen sorts before every date
      comparator: (dateA, dateB) => (dateA ? dateA.getTime() : 0) - (dateB ? dateB.getTime() : 0),
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
    },
  ];

  const defaultColDefs = {
    sortable: true,
    filter: 'agTextColumnFilter',
    resizable: true,
  };

  return (
    <Container fluid className="usacm-container-wide">
      <Row>
        <Col className="mb-3">
          Everyone who currently has access to this {courseLabel.toLowerCase()} ({studentList.length}).
          Expired and revoked access is not shown.
        </Col>
      </Row>

      <Row>
        <Col className="mb-2">
          <Form.Control type="text"
                        id="course-students-quick-filter"
                        placeholder='Search Students...'
                        name="quick-filter"
                        value={quickFilter}
                        onChange={e => quickFilterChanged(e.target.value)}/>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={studentList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => String(params.data.user_id)} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row mt-3">
          <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
        </Col>
      </Row>
    </Container>
  );
}
